import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Card,
  Group,
  Pagination,
  Skeleton,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconBuildingStore,
  IconExternalLink,
  IconRobot,
  IconSearch,
  IconUsers,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

type SortBy = 'title' | 'messages30d' | 'contacts' | 'lastActive';
type SortDir = 'asc' | 'desc';

function CentralDashboard() {
  const totals = trpc.tenancy.platform.totals.useQuery();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('messages30d');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const usage = trpc.tenancy.platform.vendorUsage.useQuery({
    search: search.trim() || undefined,
    sortBy,
    sortDir,
    page,
    pageSize,
  });

  const totalPages = usage.data ? Math.max(1, Math.ceil(usage.data.totalCount / pageSize)) : 1;

  const sortClick = (col: SortBy) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'title' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg">
        <Stack gap={4}>
          <Title order={2}>Central Dashboard</Title>
          <Text c="dimmed" size="sm">
            Platform-wide health and per-vendor usage.
          </Text>
        </Stack>

        {totals.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {totals.error.message}
          </Alert>
        )}

        {totals.isPending ? (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} height={88} />
            ))}
          </SimpleGrid>
        ) : totals.data ? (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md">
            <KpiCard label="Active vendors" value={totals.data.vendorsActive} />
            <KpiCard
              label="Trial vendors"
              value={totals.data.vendorsTrial}
              hint={`of ${totals.data.vendorsActive} active`}
            />
            <KpiCard label="Archived" value={totals.data.vendorsArchived} dim />
            <KpiCard label="Contacts (all)" value={totals.data.contactsTotal} />
            <KpiCard label="Messages 30d" value={totals.data.messages30d} />
            <KpiCard label="Active campaigns" value={totals.data.activeCampaigns} />
          </SimpleGrid>
        ) : null}

        {totals.data && (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <KpiCard
              label="Bot replies fired (30d)"
              value={totals.data.botRepliesFired30d}
              icon={<IconRobot size={16} />}
            />
            <KpiCard
              label="Messages 24h"
              value={totals.data.messages24h}
              icon={<IconUsers size={16} />}
            />
          </SimpleGrid>
        )}

        <Card p={0}>
          <Stack gap="sm" p="md" pb={0}>
            <Group justify="space-between">
              <Group gap="xs">
                <IconBuildingStore size={18} />
                <Title order={4}>Vendor usage</Title>
              </Group>
              <TextInput
                placeholder="Search vendor name or slug"
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
                w={280}
              />
            </Group>
          </Stack>

          {usage.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
              {usage.error.message}
            </Alert>
          )}

          {usage.isPending ? (
            <Skeleton height={300} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <SortHeader
                      label="Vendor"
                      col="title"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={sortClick}
                    />
                  </Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>
                    <SortHeader
                      label="Messages 30d"
                      col="messages30d"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={sortClick}
                    />
                  </Table.Th>
                  <Table.Th>
                    <SortHeader
                      label="Contacts"
                      col="contacts"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={sortClick}
                    />
                  </Table.Th>
                  <Table.Th>Active campaigns</Table.Th>
                  <Table.Th>
                    <SortHeader
                      label="Last active"
                      col="lastActive"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={sortClick}
                    />
                  </Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(usage.data?.rows ?? []).map((v) => (
                  <Table.Tr key={v.uid}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link to="/central/vendors/$uid" params={{ uid: v.uid }} {...props} />
                        )}
                      >
                        <Text fw={500}>{v.title}</Text>
                      </Anchor>
                      <Text c="dimmed" size="xs" ff="monospace">
                        {v.slug}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={v.status === 'Active' ? 'green' : 'gray'}>
                        {v.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{formatInt(v.messages30d)}</Table.Td>
                    <Table.Td>{formatInt(v.contacts)}</Table.Td>
                    <Table.Td>{formatInt(v.activeCampaigns)}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c={v.lastActive ? undefined : 'dimmed'}>
                        {v.lastActive ? formatRelative(new Date(v.lastActive)) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Open vendor">
                        <ActionIcon
                          renderRoot={(props) => (
                            <Link to="/central/vendors/$uid" params={{ uid: v.uid }} {...props} />
                          )}
                          variant="subtle"
                        >
                          <IconExternalLink size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(usage.data?.rows ?? []).length === 0 && !usage.isPending && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" ta="center" py="md">
                        No vendors match.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}

          {usage.data && usage.data.totalCount > pageSize && (
            <Group justify="space-between" p="md">
              <Text c="dimmed" size="sm">
                {usage.data.totalCount} vendors
              </Text>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </Card>
      </Stack>
    </motion.div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  icon,
  dim,
}: {
  label: string;
  value: number;
  hint?: string;
  icon?: React.ReactNode;
  dim?: boolean;
}) {
  return (
    <Card padding="sm">
      <Stack gap={2}>
        <Group gap={4}>
          {icon}
          <Text c="dimmed" size="xs" tt="uppercase" fw={500}>
            {label}
          </Text>
        </Group>
        <Text fw={700} size="xl" c={dim ? 'dimmed' : undefined}>
          {formatInt(value)}
        </Text>
        {hint && (
          <Text c="dimmed" size="xs">
            {hint}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function SortHeader({
  label,
  col,
  sortBy,
  sortDir,
  onClick,
}: {
  label: string;
  col: SortBy;
  sortBy: SortBy;
  sortDir: SortDir;
  onClick: (col: SortBy) => void;
}) {
  const active = sortBy === col;
  return (
    <Group
      gap={4}
      wrap="nowrap"
      onClick={() => onClick(col)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {active && (sortDir === 'desc' ? <IconArrowDown size={12} /> : <IconArrowUp size={12} />)}
    </Group>
  );
}

function formatInt(n: number): string {
  return n.toLocaleString();
}

function formatRelative(d: Date): string {
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export const Route = createFileRoute('/central/')({ component: CentralDashboard });
